import { escapeHtml } from '../../../utils/escapeHtml';
import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import LegendItem from '../custom-bar-chart/components/legend-item/LegendItem';
import { getChartThemeColors } from '../../../utils/chartTheme';
import { useTheme } from '../../../contexts/theme/ThemeContext';
import './StackedHorizontalBarChart.scss';

export interface StackedBarData {
  label: string;
  value: number;
  color: string;
}

interface StackedHorizontalBarChartProps {
  data: StackedBarData[];
  height?: number;
  showLegend?: boolean;
}

const StackedHorizontalBarChart: React.FC<StackedHorizontalBarChartProps> = ({
  data,
  height = 32,
  showLegend = true,
}) => {
  const { theme } = useTheme();
  const nonZeroData = data.filter((item) => item.value > 0);
  const chartOptions: EChartsOption = useMemo(
    () => {
      const chartColors = getChartThemeColors();
      return {
      grid: { top: 0, bottom: 0, left: 0, right: 0 },
      xAxis: { type: 'value', max: 100, show: false },
      yAxis: { type: 'category', data: [''], show: false },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          return `
            <div class="chart-tooltip__content chart-tooltip__content--pie">
              <div class="chart-tooltip__row">
                <div class="chart-tooltip__marker">${params.marker}</div>
                <div class="chart-tooltip__text"><span>${escapeHtml(params.seriesName)}</span></div>
                <div><strong>${params.value}%</strong></div>
              </div>
            </div>
          `;
        },
        borderRadius: 8,
        padding: [5, 10],
        borderColor: chartColors.tooltipBorder,
        backgroundColor: chartColors.tooltipBg,
        textStyle: {
          color: chartColors.tooltipText,
          fontSize: 12,
          lineHeight: 28,
          fontWeight: 600,
        },
      },
      series: data.map((item, index) => ({
        name: item.label,
        type: 'bar',
        stack: 'total',
        data: [item.value],
        itemStyle: {
          color: item.color,
          borderColor: chartColors.pieBorder,
          borderWidth: 2,
          borderRadius:
            nonZeroData.length === 1
              ? [4, 4, 4, 4]
              : index === 0
              ? [4, 0, 0, 4]
              : index === nonZeroData.length - 1
              ? [0, 4, 4, 0]
              : 0,
        },
        label: {
          show: false,
        },
      })),
    };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, theme]
  );

  return (
    <div className="custom-bar-chart-single">
      <ReactECharts key={theme} option={chartOptions} style={{ height: `${height}px`, width: '100%' }} />
      {showLegend && (
        <div className="custom-bar-chart-single__legend custom-bar-chart-single__legend--horizontal">
          {data.map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              style={{ display: 'inline-flex', alignItems: 'center', marginRight: '16px' }}
            >
              <LegendItem
                isSelected={true}
                label={item.label}
                color={item.color}
                legendItemHandler={() => {}}
              />
              <span style={{ fontSize: '11px', color: 'var(--grey-ux-500)', marginLeft: '4px' }}>({item.value}%)</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StackedHorizontalBarChart;
